import React, { useCallback, useMemo, useState } from 'react';
import {
  KiteAlert,
  KiteButton,
  KiteCard,
  KiteIcon,
  KiteModal,
  KiteTooltip,
} from '@kite/react-kite';
import './TCOServiceComparison.scss';
import AddDeviceForm from './AddDeviceForm';
import { useQueryData } from 'hooks';
import {
  TDeviceTypeData,
  TSpectrumData,
  TDiyData,
  INewTcoDeviceSelection,
  ITcoDeviceSelection,
  ITCOForm,
  ITCOSubmission,
} from 'types';
import {
  originalDeviceTypeData,
  originalSpectrumData,
  originalDiyData,
} from 'utils/defaultsAndConstants';
import DeviceCard from './DeviceCard';
import { usePostTcoDeviceSelection } from 'hooks/apiHooks/usePostTcoDeviceSelection';
import { useUpdateTcoDeviceSelection } from 'hooks/apiHooks/useUpdateTcoDeviceSelection';
import { useDeleteTcoDeviceSelection } from 'hooks/apiHooks/useDeleteTcoDeviceSelection';
import { tcoFieldsIds } from 'utils/defaultsAndConstants/constants';
import { checkFormErrors } from 'utils';
import SpectrumDiyTotal from 'components/TCOComponents/TCOOverview/SpectrumDiyTotal';
import { tcoFormattedPrice } from 'utils/defaultsAndConstants/tcoConstants';
import { useRefreshTcoDeviceSelections } from 'hooks/useRefreshTcoDeviceSelections';

export interface ITCOServiceComparison {
  currentForms: ITCOForm[];
  /** Submission data for current form */
  tcoSubmissions: ITCOSubmission[];
}

const TCOServiceComparison = ({
  currentForms,
  tcoSubmissions,
}: ITCOServiceComparison) => {
  // =============================================
  // State/Ref/Hooks
  // =============================================
  const {
    allTcoSpectrumProducts,
    allTcoDiyProducts,
    tcoDeviceSelections,
    currentEstimate,
    userId,
  } = useQueryData();

  const {
    varDiscountFieldId,
    salesTaxFieldId,
    shippingHandlingFieldId,
    termsFieldId,
  } = tcoFieldsIds;

  const { postTcoDeviceSelection } = usePostTcoDeviceSelection(
    currentEstimate?.id || ''
  );

  const { updateTcoDeviceSelection } = useUpdateTcoDeviceSelection(
    currentEstimate?.id || ''
  );

  const { deleteTcoDeviceSelection } = useDeleteTcoDeviceSelection(
    currentEstimate?.id || ''
  );

  const { refreshDeviceSelections } = useRefreshTcoDeviceSelections(
    currentEstimate?.id || ''
  );

  const [showForm, setShowForm] = useState(false);
  const [deviceTypeData, setDeviceTypeData] = useState<TDeviceTypeData>(
    originalDeviceTypeData
  );
  const [spectrumData, setSpectrumData] =
    useState<TSpectrumData>(originalSpectrumData);
  const [diyData, setDiyData] = useState<TDiyData>(originalDiyData);
  const [formErrors, setFormErrors] = useState({
    ...originalDeviceTypeData,
    ...originalSpectrumData,
    ...originalDiyData,
  });
  const [globalError, setGlobalError] = useState(false);

  // =============================================
  // Helpers
  // =============================================

  const varDiscount =
    1 -
    Number(
      tcoSubmissions &&
        tcoSubmissions.find((s) => s.fieldId === varDiscountFieldId)?.answer
    ) /
      100;

  const salesTax =
    Number(
      tcoSubmissions &&
        tcoSubmissions.find((s) => s.fieldId === salesTaxFieldId)?.answer
    ) / 100;

  const shippingHandeling =
    Number(
      tcoSubmissions &&
        tcoSubmissions.find((s) => s.fieldId === shippingHandlingFieldId)
          ?.answer
    ) / 100;

  const handleFormError = useCallback(() => {
    const { hasErrors: isDeviceError, errorObject: deviceErrors } =
      checkFormErrors(deviceTypeData, ['deviceType', 'quantity'], {
        quantity: 'number',
      });

    const { hasErrors: isSpectrumError, errorObject: spectrumErrors } =
      checkFormErrors(
        spectrumData,
        ['spectrumId', 'monthlyCost', 'installationCost'],
        {
          spectrumId: 'select',
        }
      );

    const { hasErrors: isDiyError, errorObject: diyErrors } = checkFormErrors(
      diyData,
      ['hardwareCost', 'licensingCost']
    );

    const combinedErrors = {
      ...deviceErrors,
      ...spectrumErrors,
      ...diyErrors,
    };

    if (isDeviceError || isSpectrumError || isDiyError) {
      setGlobalError(true);
      setFormErrors({ ...formErrors, ...combinedErrors });
    }

    return isDeviceError || isSpectrumError || isDiyError;
  }, [formErrors, deviceTypeData, spectrumData, diyData]);

  const resetFormData = useCallback(() => {
    setDeviceTypeData(originalDeviceTypeData);
    setSpectrumData(originalSpectrumData);
    setDiyData(originalDiyData);

    setGlobalError(false);

    setFormErrors({
      ...originalDeviceTypeData,
      ...originalSpectrumData,
      ...originalDiyData,
    });
  }, []);

  const toggleEditModal = useCallback(() => {
    if (showForm) {
      resetFormData();
    }
    setShowForm(!showForm);
  }, [showForm, resetFormData]);

  const handleAddDevice = useCallback(() => {
    const { id, deviceType, quantity } = deviceTypeData;

    if (id) {
      updateTcoDeviceSelection({
        id: id,
        estimateId: currentEstimate?.id || '',
        name: deviceType,
        diyId: diyData.diyId
          ? diyData.diyId
          : 'a3b8ab0a-7131-42d9-95c5-b684a6b1e7f5',
        spectrumId: spectrumData.spectrumId,
        quantity: Number(quantity),
        monthlyCost: Number(spectrumData.monthlyCost),
        installationCost: Number(spectrumData.installationCost),
        hardwareCost: Number(diyData.hardwareCost),
        licenseCost: Number(diyData.licensingCost),
        updatedBy: userId,
      });
    } else {
      const newDeviceSelection: INewTcoDeviceSelection = {
        estimateId: currentEstimate?.id || '',
        name: deviceTypeData.deviceType,
        diyId: diyData.diyId
          ? diyData.diyId
          : 'a3b8ab0a-7131-42d9-95c5-b684a6b1e7f5',
        spectrumId: spectrumData.spectrumId,
        quantity: Number(deviceTypeData.quantity),
        monthlyCost: Number(spectrumData.monthlyCost),
        installationCost: Number(spectrumData.installationCost),
        hardwareCost: Number(diyData.hardwareCost),
        licenseCost: Number(diyData.licensingCost || 0),
        updatedBy: userId,
      };

      postTcoDeviceSelection(newDeviceSelection);
    }

    toggleEditModal();
  }, [
    currentEstimate,
    spectrumData,
    diyData,
    userId,
    deviceTypeData,
    postTcoDeviceSelection,
    toggleEditModal,
    updateTcoDeviceSelection,
  ]);

  const handleSubmit = useCallback(() => {
    if (!handleFormError()) {
      handleAddDevice();
    }
  }, [handleFormError, handleAddDevice]);

  const handleEditDevice = useCallback(
    (updatedSelection: ITcoDeviceSelection) => {
      const {
        id,
        name: deviceType,
        quantity,
        spectrumId,
        diyId,
        installationCost,
        hardwareCost,
        monthlyCost,
        licenseCost,
      } = updatedSelection;

      const diyProduct = allTcoDiyProducts.find((p) => p.id === diyId);

      const termFieldValue = tcoSubmissions.find(
        (s) => s.fieldId === termsFieldId
      )?.answer;

      setDeviceTypeData({ id, deviceType, quantity: quantity.toString() });
      setSpectrumData({
        spectrumId,
        monthlyCost: monthlyCost.toString(),
        installationCost: installationCost.toString(),
      });
      setDiyData({
        diyId,
        diyDeviceType: diyProduct?.familyType!,
        deviceDescription: diyProduct?.description!,
        diyModel: diyProduct?.name!,
        hardwareCost: hardwareCost.toString(),
        licenseType: diyProduct?.licenseType!,
        licenseTerm:
          diyProduct?.licenseType !== 'N/A' ? termFieldValue! : 'N/A',
        licensingCost: licenseCost.toString(),
        licenseDescription: diyProduct?.licenseDescription!,
      });

      toggleEditModal();
    },
    [allTcoDiyProducts, toggleEditModal, tcoSubmissions, termsFieldId]
  );

  const handleDeleteDevice = useCallback(
    (deletedSelection: ITcoDeviceSelection) => {
      deleteTcoDeviceSelection(deletedSelection);
    },
    [deleteTcoDeviceSelection]
  );

  // =============================================
  // Interaction Handlers
  // =============================================

  const onSpectrumDataChange = useCallback(
    (productValues: Partial<TSpectrumData>) => {
      const { installationCost, monthlyCost } = productValues;

      setSpectrumData((prevData) => ({
        ...prevData,
        ...productValues,
      }));

      setFormErrors((prev) => ({
        ...prev,
        installationCost: installationCost ? '' : prev.installationCost,
        monthlyCost: monthlyCost ? '' : prev.monthlyCost,
      }));
    },
    []
  );

  const {
    totalMonthlyCost,
    totalInstallationCost,
    totalHardwareCost,
    totalLicensingCost,
  } = useMemo(() => {
    return tcoDeviceSelections.reduce(
      (totals, selection) => {
        const {
          monthlyCost,
          installationCost,
          hardwareCost,
          licenseCost,
          quantity,
        } = selection;
        totals.totalMonthlyCost += monthlyCost * quantity;
        totals.totalInstallationCost += installationCost * quantity;
        totals.totalHardwareCost += hardwareCost * quantity;
        totals.totalLicensingCost += licenseCost * quantity;
        return totals;
      },
      {
        totalMonthlyCost: 0,
        totalInstallationCost: 0,
        totalHardwareCost: 0,
        totalLicensingCost: 0,
      }
    );
  }, [tcoDeviceSelections]);

  const onDiyDataChange = useCallback((productValues: Partial<TDiyData>) => {
    setDiyData((prevData) => ({ ...prevData, ...productValues }));
    setFormErrors((prev) => ({
      ...prev,
      licensingCost: productValues.licensingCost ? '' : prev.licensingCost,
      hardwareCost: productValues.hardwareCost ? '' : prev.hardwareCost,
    }));
  }, []);

  const onDeviceDataChange = useCallback(
    (deviceValues?: Partial<TDeviceTypeData>) => {
      if (deviceValues) {
        setDeviceTypeData({ ...deviceTypeData, ...deviceValues });
      } else {
        setDeviceTypeData(originalDeviceTypeData);
      }
    },
    [deviceTypeData]
  );

  const handleFormDataChange = useCallback(
    (type: 'deviceType' | 'spectrumProduct' | 'diyProduct') =>
      (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;

        const formattedValue = value.replace(/[$%,]/g, '');

        if (formattedValue && formattedValue !== '') {
          setFormErrors((prev) => ({
            ...prev,
            [name]: '',
          }));
        }

        if (type === 'deviceType') {
          onDeviceDataChange({ [name]: value });
        } else if (type === 'spectrumProduct') {
          if (name === 'spectrumId') {
            const termFieldValue = tcoSubmissions.find(
              (s) => s.fieldId === termsFieldId
            )?.answer;

            const currentSpectrumProduct = allTcoSpectrumProducts.find(
              (p) => p.id === formattedValue
            );

            const monthlyCost = currentSpectrumProduct?.prices.find(
              (p) => p.term === termFieldValue
            )?.price;

            const installationCost = currentSpectrumProduct?.rateNrc;

            const currentDiyProduct = allTcoDiyProducts.find(
              (p) => p.spectrumProductId === currentSpectrumProduct?.id
            );

            const diyHardwareCost = (
              currentDiyProduct?.rateNrc! * varDiscount +
              currentDiyProduct?.rateNrc! * varDiscount * salesTax +
              currentDiyProduct?.rateNrc! * varDiscount * shippingHandeling
            ).toFixed(2);

            const licenseCost = (
              currentDiyProduct?.prices.find((p) => p.term === termFieldValue)
                ?.price! *
                varDiscount +
              currentDiyProduct?.prices.find((p) => p.term === termFieldValue)
                ?.price! *
                varDiscount *
                salesTax +
              currentDiyProduct?.prices.find((p) => p.term === termFieldValue)
                ?.price! *
                varDiscount *
                shippingHandeling
            ).toFixed(2);

            onSpectrumDataChange({
              [name]: formattedValue,
              installationCost: installationCost?.toString(),
              monthlyCost: monthlyCost?.toString(),
            });

            onDiyDataChange({
              diyId: currentDiyProduct
                ? currentDiyProduct?.id
                : 'a3b8ab0a-7131-42d9-95c5-b684a6b1e7f5',
              diyDeviceType: currentDiyProduct
                ? currentDiyProduct?.familyType
                : 'N/A',
              diyModel: currentDiyProduct ? currentDiyProduct?.name : 'N/A',
              hardwareCost: isNaN(Number(diyHardwareCost))
                ? '0.00'
                : diyHardwareCost,
              licenseTerm:
                currentDiyProduct && currentDiyProduct?.licenseType !== 'N/A'
                  ? termFieldValue!
                  : 'N/A',
              licenseType: currentDiyProduct
                ? currentDiyProduct?.licenseType
                : 'N/A',
              licenseDescription: currentDiyProduct
                ? currentDiyProduct?.licenseDescription
                : 'N/A',
              deviceDescription: currentDiyProduct
                ? currentDiyProduct?.description!
                : 'N/A',
              licensingCost: isNaN(Number(licenseCost)) ? '0.00' : licenseCost,
            });
          } else {
            onSpectrumDataChange({ [name]: formattedValue });
          }
        } else if (type === 'diyProduct') {
          onDiyDataChange({ [name]: formattedValue });
        }

        setGlobalError(false);
      },
    [
      salesTax,
      shippingHandeling,
      varDiscount,
      termsFieldId,
      onSpectrumDataChange,
      onDeviceDataChange,
      onDiyDataChange,
      allTcoDiyProducts,
      allTcoSpectrumProducts,
      tcoSubmissions,
    ]
  );

  const renderDeviceCards = useCallback(() => {
    return tcoDeviceSelections.map((selections) => {
      return (
        <div className="device-card" key={selections.id}>
          <DeviceCard
            selection={selections}
            tcoSpectrumProducts={allTcoSpectrumProducts}
            tcoDiyProducts={allTcoDiyProducts}
            onEdit={handleEditDevice}
            onDelete={handleDeleteDevice}
            isPublished={
              allTcoSpectrumProducts.find((p) => p.id === selections.spectrumId)
                ?.published || false
            }
          />
        </div>
      );
    });
  }, [
    tcoDeviceSelections,
    allTcoDiyProducts,
    allTcoSpectrumProducts,
    handleDeleteDevice,
    handleEditDevice,
  ]);

  return (
    <KiteCard className="service-comparison">
      <div className="service-comparison-title-container">
        <h2>Service Comparison</h2>
        <div className="refresh-button">
          <KiteButton
            type="standalone-link"
            size="small"
            onClick={() =>
              refreshDeviceSelections({
                tcoDeviceSelections,
                tcoSubmissions,
                refresh: true,
              })
            }
          >
            <KiteIcon name="refresh" size="22px" margin="0 0 0 0" />
            Refresh
          </KiteButton>
          <KiteTooltip>
            Clicking on refresh button will reset the device selections prices
            to original values.
          </KiteTooltip>
        </div>
      </div>
      {tcoDeviceSelections.length === 0 ? <p>No devices added yet.</p> : null}
      <div className="device-cards-list">{renderDeviceCards()}</div>
      {tcoDeviceSelections.length < 20 ? (
        <div className="service-comparison-button">
          <KiteButton type="outline" onClick={() => setShowForm(true)}>
            + Add Device
          </KiteButton>
          <p className="service-comparison-button-p"> Limit 20 devices types</p>
        </div>
      ) : (
        <p>You've reached your limit of 20 device types.</p>
      )}
      <KiteModal
        title={deviceTypeData.id ? 'Edit Device Type' : 'Add Device Type'}
        canShow={showForm}
        onHide={toggleEditModal}
        ctaCopy={deviceTypeData.id ? 'Save' : 'Add'}
        ctaAction={handleSubmit}
        secondaryCtaCopy="Cancel"
        secondaryCtaAction={toggleEditModal}
        className="service-comparison-modal"
        disableBackgroundClose={true}
      >
        {globalError && (
          <KiteAlert
            description="Please fill out the missing fields"
            type="alert"
            className="service-comparison__alert"
          />
        )}
        <AddDeviceForm
          deviceData={deviceTypeData}
          spectrumData={spectrumData}
          diyData={diyData}
          onChange={handleFormDataChange}
          spectrumProducts={allTcoSpectrumProducts.filter((p) => p.published)}
          diyProducts={allTcoDiyProducts.filter((p) => p.published)}
          formErrors={formErrors}
        />
      </KiteModal>
      <div className="service-comparison-total">
        <SpectrumDiyTotal
          title="Spectrum Total"
          primaryCostLabel="Total Monthly Cost"
          primaryCost={`$${tcoFormattedPrice(totalMonthlyCost)}`}
          secondaryCostLabel="Total Installation Cost"
          secondaryCost={`$${tcoFormattedPrice(totalInstallationCost)}`}
        />
        <SpectrumDiyTotal
          title="Diy Total"
          primaryCostLabel="Total Hardware Cost"
          primaryCost={`$${tcoFormattedPrice(totalHardwareCost)}`}
          secondaryCostLabel="Total Licensing Cost"
          secondaryCost={`$${tcoFormattedPrice(totalLicensingCost)}`}
          isDiy={true}
        />
      </div>
    </KiteCard>
  );
};

export default TCOServiceComparison;
